import { FC, useEffect, useMemo, useRef } from "react"
import { AdminFormFieldSubmit } from "../admin/formFields"
import FTModal from "./FTModal"
import ScreenDashboardAdminGroupsHierarchy from "../../pages/admins/groups/groups.hierarchy"
import { useDispatch } from "react-redux"
import { setModalsData, TModalsState } from "../../api/store/commonSlice"
import { useSelector } from "react-redux"
import { HGSelectorProvider, useHGSelectorContext } from "./hierarchyGroupSelector.context"
import { useTranslation } from "react-i18next"

type THierarchyGroupSelectorProps = {
  initiallySelectedGroups: string[];
  onSelectionChange: (selectedGroups: string[]) => void;
  selectLimit?: number;
  renderAboveImpersonationBar?: boolean;
}

// manage selected group values - this component has access to useHGSelectorContext
const HGContextEnabledSelectionValuesController: FC<Partial<THierarchyGroupSelectorProps>> = ({
  initiallySelectedGroups = [],
  onSelectionChange,
  selectLimit,
}) => {
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);

  const {
    selectedHGroups,
    setSelectedHGroups,
    setSelectLimit,
  } = useHGSelectorContext();

  // local variable for selected groups, gets updated only on close of modal / unmount of component
  const localSelectedHGroups = useRef<string[]>([]);

  // set initially selected groups
  useEffect(() => {
    localSelectedHGroups.current = initiallySelectedGroups;
    setSelectedHGroups(initiallySelectedGroups);
    setSelectLimit?.(selectLimit);
    return () => {
      if (modalsState.showReadOnlyViewForHierarchyGroup) return;
      onSelectionChange?.(localSelectedHGroups?.current || []);
      setSelectLimit?.(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // initialise selected groups when modal is opened
    if (modalsState.showHierarchyGroupSelector) {
      setSelectedHGroups(initiallySelectedGroups);
      setSelectLimit?.(selectLimit);
    }
  }, [modalsState.showHierarchyGroupSelector]);

  // call onSelectionChange on selected groups change
  useEffect(() => {
    localSelectedHGroups.current = selectedHGroups;
    // limit selection to selectLimit
    if (selectLimit && selectedHGroups.length > selectLimit) {
      setSelectedHGroups(selectedHGroups.slice(0, selectLimit));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHGroups]);

  return <></>
}

// This component is used to display the hierarchy group selector popup
const HierarchyGroupSelector: FC<THierarchyGroupSelectorProps> = ({
  initiallySelectedGroups = [],
  onSelectionChange,
  selectLimit,
  renderAboveImpersonationBar
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'admins.groups.hierarchy' });
  const dispatch = useDispatch();
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);

  const MemoizedHGContextEnabledSelectionValuesController = useMemo(() => (
    <HGContextEnabledSelectionValuesController
      initiallySelectedGroups={initiallySelectedGroups}
      onSelectionChange={onSelectionChange}
      selectLimit={selectLimit}
    />
  ), [initiallySelectedGroups, onSelectionChange]);

  return (
    <>
      <HGSelectorProvider>
        {modalsState.showHierarchyGroupSelector && (<>{MemoizedHGContextEnabledSelectionValuesController}</>)}
        <FTModal
          dismissible={false}
          heading={''} // not in use, instead using custom header in ScreenDashboardAdminGroupsHierarchy
          visible={modalsState.showHierarchyGroupSelector}
          onHide={() => { dispatch(setModalsData({ ...modalsState, showHierarchyGroupSelector: false, })) }}
          containerStyle="w-[calc(100vw-4rem)] group/hierarchy-modal"
          renderAboveImpersonationBar={renderAboveImpersonationBar}
        >
          <ScreenDashboardAdminGroupsHierarchy />
        </FTModal>
      </HGSelectorProvider>
    </>
  )
}
export default HierarchyGroupSelector